import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/lib/i18n-context";
import Countdown from "@/components/Countdown";
import { Button } from "@/components/ui/button";
import { ArrowRight, MapPin, Mic, UtensilsCrossed, Sparkles, Users, Trophy, Award } from "lucide-react";

const heroImg = "/ijff_hero_aerial.jpg";

const PROGRAMS = [
  { key: "prog_music", icon: Mic, span: "md:col-span-7 md:row-span-2",
    img: "/ijff_stage.jpg" },
  { key: "prog_cuisine", icon: UtensilsCrossed, span: "md:col-span-5",
    img: "/ijff_food_2025.jpg" },
  { key: "prog_bm", icon: Users, span: "md:col-span-5",
    img: "https://customer-assets.emergentagent.com/job_f15d1a1b-1b99-454c-90f2-2714dda00985/artifacts/df73eeev_the%20situation%20of%20business%20batching.jpeg" },
  { key: "prog_cosplay", icon: Sparkles, span: "md:col-span-4",
    img: "https://images.unsplash.com/photo-1613376023733-0a73315d9b06?w=1200&q=80" },
  { key: "prog_speech", icon: Trophy, span: "md:col-span-4",
    img: "https://images.unsplash.com/photo-1475721027785-f74eccf877e2?w=1200&q=80" },
  { key: "prog_stamp", icon: Award, span: "md:col-span-4",
    img: "/ijff_hero_aerial.jpg" },
];

export default function HomePage() {
  const { t } = useLang();
  return (
    <div>
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-end overflow-hidden" data-testid="hero-section">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${heroImg})` }}
        />
        <div className="absolute inset-0 gradient-hero-overlay" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 pb-16 md:pb-24 pt-32 w-full">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 text-white/90 border border-white/30 rounded-full px-4 py-1.5 text-xs md:text-sm font-medium mb-6 backdrop-blur-sm bg-white/5">
              <MapPin className="w-3.5 h-3.5" />
              {t("hero_kicker")}
            </div>
            <h1 className="font-display font-extrabold text-white text-5xl md:text-7xl lg:text-8xl leading-[1.02] text-shadow-hero" data-testid="hero-title">
              <span className="block">{t("hero_title_1")}</span>
              <span className="block text-brand-sakura">{t("hero_title_2")}</span>
            </h1>
            <p className="text-white/90 text-base md:text-xl mt-6 max-w-2xl text-shadow-hero">
              {t("hero_sub")}
            </p>
            <div className="mt-10">
              <Countdown />
            </div>
            <div className="mt-10 flex flex-wrap gap-3">
              <Button asChild size="lg" className="h-12 rounded-full bg-brand-red hover:bg-brand-red-dark text-white px-6" data-testid="hero-register-btn">
                <Link to="/register">
                  {t("cta_register")} <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="h-12 rounded-full bg-white/10 backdrop-blur-md border-white/40 text-white hover:bg-white hover:text-brand-red" data-testid="hero-map-btn">
                <Link to="/map">{t("cta_map")}</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* CONCEPT */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-24 grid md:grid-cols-12 gap-10 items-start">
        <div className="md:col-span-5">
          <div className="text-xs uppercase tracking-widest text-brand-red font-semibold mb-3">
            2026 · Nations Bridge
          </div>
          <h2 className="font-display text-4xl md:text-5xl font-bold leading-tight">
            {t("concept_title")}
          </h2>
        </div>
        <div className="md:col-span-7">
          <p className="text-lg md:text-xl leading-relaxed text-foreground/80">
            {t("concept_body")}
          </p>
          <div className="mt-6 text-xs uppercase tracking-widest text-muted-foreground">
            Organized by <span className="text-foreground font-semibold">Nations Bridge Event Management &amp; Planner</span> · Supported by <span className="text-brand-red font-semibold">Embassy of the Republic of Indonesia</span>
          </div>
          <div className="mt-8 grid grid-cols-3 gap-6 text-sm">
            <div>
              <div className="font-display text-3xl font-bold text-brand-red">68+</div>
              <div className="text-muted-foreground mt-1">Years of Friendship</div>
            </div>
            <div>
              <div className="font-display text-3xl font-bold text-brand-red">100+</div>
              <div className="text-muted-foreground mt-1">Booths</div>
            </div>
            <div>
              <div className="font-display text-3xl font-bold text-brand-red">2 Days</div>
              <div className="text-muted-foreground mt-1">Dec 12 – 13</div>
            </div>
          </div>
        </div>
      </section>

      {/* PROGRAMS BENTO */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-16" data-testid="programs-section">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="text-xs uppercase tracking-widest text-brand-red font-semibold mb-3">
              Programs
            </div>
            <h2 className="font-display text-4xl md:text-5xl font-bold">{t("programs_title")}</h2>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-12 auto-rows-[220px] gap-4">
          {PROGRAMS.map((p) => {
            const Icon = p.icon;
            return (
              <div
                key={p.key}
                className={`relative overflow-hidden rounded-3xl group cursor-pointer ${p.span}`}
                data-testid={`program-${p.key}`}
              >
                <div
                  className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-105"
                  style={{ backgroundImage: `url(${p.img})` }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                <div className="relative z-10 h-full flex flex-col justify-between p-5">
                  <div className="flex items-center justify-between">
                    <div className="w-9 h-9 rounded-full bg-white/20 backdrop-blur-md border border-white/40 flex items-center justify-center">
                      <Icon className="w-4 h-4 text-white" />
                    </div>
                    {p.soon && (
                      <span className="text-[10px] uppercase tracking-widest bg-brand-gold text-black font-bold px-2.5 py-1 rounded-full">
                        {t("coming_soon")}
                      </span>
                    )}
                  </div>
                  <div className="text-white font-display text-2xl md:text-3xl font-bold text-shadow-hero">
                    {t(p.key)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* PARTICIPATE STRIP */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-24" data-testid="participate-section">
        <div className="grid md:grid-cols-2 gap-4">
          <Link to="/map" className="group relative overflow-hidden rounded-3xl bg-brand-indigo text-white p-10 min-h-[280px] flex flex-col justify-between hover:-translate-y-1 transition-transform">
            <div>
              <div className="text-xs uppercase tracking-widest text-white/70 mb-3">01</div>
              <h3 className="font-display text-3xl md:text-4xl font-bold leading-tight">
                Booth Map<br/>会場マップ
              </h3>
            </div>
            <div className="flex items-center gap-2 text-sm font-semibold">
              Explore 100+ booths <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </div>
            <div className="absolute -right-8 -bottom-8 w-64 h-64 rounded-full bg-white/10" />
          </Link>
          <Link to="/register" className="group relative overflow-hidden rounded-3xl bg-brand-red text-white p-10 min-h-[280px] flex flex-col justify-between hover:-translate-y-1 transition-transform">
            <div>
              <div className="text-xs uppercase tracking-widest text-white/70 mb-3">02</div>
              <h3 className="font-display text-3xl md:text-4xl font-bold leading-tight">
                Register<br/>参加登録
              </h3>
              <p className="mt-3 text-sm text-white/80 max-w-sm">
                Performer · Restaurant · Trader · Business Matching · Sponsor
              </p>
            </div>
            <div className="flex items-center gap-2 text-sm font-semibold">
              {t("deadline")} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </div>
            <div className="absolute -right-8 -bottom-8 w-64 h-64 rounded-full bg-white/10" />
          </Link>
        </div>
      </section>

      {/* SPONSOR STRIP */}
      <section className="bg-brand-canvas/60 border-y border-border/70" data-testid="sponsors-strip">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-16 grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-4">
            <div className="text-xs uppercase tracking-widest text-brand-red font-semibold mb-2">Partnership</div>
            <h2 className="font-display text-3xl md:text-4xl font-bold">{t("sponsors_title")}</h2>
          </div>
          <div className="md:col-span-6 text-muted-foreground">
            <p>From Partner (15M JPY) down to Bronze — align your brand with the largest Indonesia-Japan cultural gathering in Tokyo.</p>
          </div>
          <div className="md:col-span-2">
            <Button asChild className="w-full rounded-full bg-black text-white hover:bg-brand-red" data-testid="home-sponsor-cta">
              <Link to="/sponsors">See tiers</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
